/*
 * SystemEnvironmentsCard - 环境配置概览卡片
 * 展示 System 的 TEST/STAGING/PROD 环境配置信息
 */

import React from 'react';
import {
  Box,
  Card,
  CardContent,
  CardHeader,
  Chip,
  Grid,
  Typography,
  Tooltip,
  makeStyles,
  createStyles,
  Theme,
  Divider,
} from '@material-ui/core';
import CloudIcon from '@material-ui/icons/Cloud';
import StorageIcon from '@material-ui/icons/Storage';
import FolderIcon from '@material-ui/icons/Folder';
import { useEntity } from '@backstage/plugin-catalog-react';
import { EmptyState } from '@backstage/core-components';
import {
  parseEnvironmentConfigs,
  getArgoCDProjectInfo,
  isKnativeManaged,
  environmentColors,
  EnvironmentConfig,
} from './types';

const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    card: {
      height: '100%',
    },
    envCard: {
      borderRadius: theme.spacing(1),
      border: '2px solid',
      padding: theme.spacing(2),
      height: '100%',
      transition: 'transform 0.2s, box-shadow 0.2s',
      '&:hover': {
        transform: 'translateY(-2px)',
        boxShadow: theme.shadows[4],
      },
    },
    envHeader: {
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'space-between',
      marginBottom: theme.spacing(2),
    },
    envLabel: {
      fontWeight: 700,
      fontSize: '0.875rem',
      letterSpacing: '0.05em',
    },
    infoRow: {
      display: 'flex',
      alignItems: 'center',
      gap: theme.spacing(1),
      marginBottom: theme.spacing(1),
      '& svg': {
        fontSize: '1rem',
        opacity: 0.7,
      },
    },
    infoLabel: {
      fontSize: '0.75rem',
      color: theme.palette.text.secondary,
      minWidth: 70,
    },
    infoValue: {
      fontSize: '0.875rem',
      fontFamily: 'monospace',
      wordBreak: 'break-all',
    },
    argocdInfo: {
      marginTop: theme.spacing(2),
      padding: theme.spacing(1.5),
      backgroundColor: theme.palette.background.default,
      borderRadius: theme.spacing(0.5),
    },
    argocdLabel: {
      fontSize: '0.75rem',
      color: theme.palette.text.secondary,
      marginBottom: theme.spacing(0.5),
    },
    argocdValue: {
      fontSize: '0.875rem',
      fontWeight: 500,
    },
  }),
);

interface EnvironmentCardProps {
  config: EnvironmentConfig;
}

const EnvironmentCard: React.FC<EnvironmentCardProps> = ({ config }) => {
  const classes = useStyles();
  const colors = environmentColors[config.name] || environmentColors.test;

  return (
    <Box
      className={classes.envCard}
      style={{
        backgroundColor: colors.bg,
        borderColor: colors.border,
      }}
    >
      <Box className={classes.envHeader}>
        <Chip
          label={config.label}
          size="small"
          style={{
            backgroundColor: colors.border,
            color: '#fff',
            fontWeight: 600,
          }}
        />
        <CloudIcon style={{ color: colors.text, opacity: 0.6 }} />
      </Box>

      <Box className={classes.infoRow}>
        <StorageIcon style={{ color: colors.text }} />
        <Typography className={classes.infoLabel}>集群</Typography>
        <Tooltip title={config.server} arrow>
          <Typography className={classes.infoValue} style={{ color: colors.text }}>
            {config.clusterName}
          </Typography>
        </Tooltip>
      </Box>

      <Box className={classes.infoRow}>
        <FolderIcon style={{ color: colors.text }} />
        <Typography className={classes.infoLabel}>命名空间</Typography>
        <Typography className={classes.infoValue} style={{ color: colors.text }}>
          {config.namespace}
        </Typography>
      </Box>
    </Box>
  );
};

export const SystemEnvironmentsCard: React.FC = () => {
  const classes = useStyles();
  const { entity } = useEntity();

  const isKnative = isKnativeManaged(entity);
  const environments = parseEnvironmentConfigs(entity);
  const argocdProject = getArgoCDProjectInfo(entity);

  if (environments.length === 0) {
    const emptyDescription = isKnative
      ? '此 System 尚未配置环境信息。请在 annotations 中添加 hose.io/knative-dest-* 配置。'
      : '此 System 尚未配置环境信息。请在 annotations 中添加 hose.io/argocd-dest-* 配置。';
    
    return (
      <Card className={classes.card}>
        <CardHeader title="环境配置" />
        <CardContent>
          <EmptyState
            missing="info"
            title="未配置环境"
            description={emptyDescription}
          />
        </CardContent>
      </Card>
    );
  }

  return (
    <Card className={classes.card}>
      <CardHeader 
        title="环境配置" 
        subheader={`${environments.length} 个环境`}
      />
      <CardContent>
        <Grid container spacing={2}>
          {environments.map((env) => (
            <Grid item xs={12} sm={6} md={4} key={env.name}>
              <EnvironmentCard config={env} />
            </Grid>
          ))}
        </Grid>

        {!isKnative && argocdProject && (
          <>
            <Divider style={{ margin: '16px 0' }} />
            <Box className={classes.argocdInfo}>
              <Typography className={classes.argocdLabel}>
                关联 ArgoCD Project
              </Typography>
              <Typography className={classes.argocdValue}>
                {argocdProject.instanceName} / {argocdProject.projectName}
              </Typography>
            </Box>
          </>
        )}
        {isKnative && (
          <>
            <Divider style={{ margin: '16px 0' }} />
            <Box className={classes.argocdInfo}>
              <Typography className={classes.argocdLabel}>
                管理方式
              </Typography>
              <Typography className={classes.argocdValue}>
                Knative Serverless
              </Typography>
            </Box>
          </>
        )}
      </CardContent>
    </Card>
  );
};

